import type { ReactElement } from "react";

import { useSelector } from "react-redux";

import { IconButton, PlainTooltip, Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { NT_PURCHASE_PAGE_LINK } from "app/pages/sales-intelligence/constants/common";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import trackingService from "../../../services/tracking/trackingService";
import { selectIsOnlyRevealedShown } from "../../../store/selectors";

import { StyledButton, StyledTooltipContent } from "./styles";

type RevealActionButtonProps = {
    remainingRevealQuota: number;
    numberOfResults: number;
    areSearchResultsFetching: boolean;
    isContactsRevealAccess: boolean;
    isContactReadonlyAccess: boolean;
    onClick(): void;
};

const RevealActionButton = ({
    remainingRevealQuota,
    numberOfResults,
    areSearchResultsFetching,
    isContactsRevealAccess,
    isContactReadonlyAccess,
    onClick,
}: RevealActionButtonProps): ReactElement => {
    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;
    const isOnlyRevealedShown = useSelector(selectIsOnlyRevealedShown);

    const resultsAreFetchingOrEmpty = areSearchResultsFetching || numberOfResults <= 0;
    const isDisabled =
        (!isContactReadonlyAccess && !isContactsRevealAccess) ||
        resultsAreFetchingOrEmpty ||
        isOnlyRevealedShown ||
        (!isDataCreditsEnabled && remainingRevealQuota <= 0);

    function handleClick() {
        trackingService.trackRevealOptionsClicked();
        onClick();
    }

    const handleUpsellRevealButtonClick = () => {
        trackingService.trackTrialRevealOptionsClicked(NT_PURCHASE_PAGE_LINK);
        window.open(NT_PURCHASE_PAGE_LINK);
    };

    function renderWithCorrectTooltip() {
        const button = (
            <div>
                <IconButton
                    type="flat"
                    iconName="eye-icon-blue"
                    isDisabled={isDisabled}
                    onClick={handleClick}
                    dataAutomation="contacts-actions-panel.reveal-action-button"
                >
                    {translate("si.advanced_search.contacts_search.action.reveal.text")}
                </IconButton>
            </div>
        );

        if (!isDataCreditsEnabled) {
            if (isContactReadonlyAccess && remainingRevealQuota <= 0) {
                return (
                    <IconButton
                        iconName="sparkles"
                        type="flatTrial"
                        onClick={handleUpsellRevealButtonClick}
                    >
                        {translate("si.advanced_search.contacts_search.action.reveal.text")}
                    </IconButton>
                );
            }

            if (isContactsRevealAccess && remainingRevealQuota <= 0) {
                return (
                    <PlainTooltip
                        maxWidth={267}
                        variation="white"
                        tooltipContent={
                            <StyledTooltipContent>
                                {translate("si.advanced_search.results_section.contacts.button.reveal.quota_tooltip")}
                            </StyledTooltipContent>
                        }
                    >
                        {button}
                    </PlainTooltip>
                );
            }
        }

        return (
            <Tooltip
                enabled={!isDisabled}
                content={translate("si.advanced_search.contacts_search.action.reveal.tooltip")}
            >
                {button}
            </Tooltip>
        );
    }

    return <StyledButton>{renderWithCorrectTooltip()}</StyledButton>;
};

export default RevealActionButton;
